import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { NzI18nService, NZ_I18N, en_US } from 'ng-zorro-antd/i18n';
import { NoteService } from './features/manage-settings/notes/note.service';
import { AuthService } from './core/services/auth.service';
import {
  Observable,
  Subject,
  takeUntil,
  filter,
  distinctUntilChanged,
  map,
  combineLatest,
  debounceTime,
  share,
  switchMap,
  timer,
} from 'rxjs';
import { OrganizationDetailModel } from './features/organizations/organization.interface';
import { ReportLabelsService } from './features/manage-settings/report-labels/report-labels.service';
import { ReportLabel } from './features/manage-settings/report-labels/report-labels.model';
import { AppService } from './core/services/app.service';
import { BroadcastService } from './features/broadcast-config/broadcast.service';
import { Broadcast } from './features/broadcast-config/broadcast';
import { CommonModule } from '@angular/common';
import { BroadcastNotificationService } from './core/services/broadcast-notification.service';
import { CommonService } from './shared/services/common.service';
import { SettingsService } from './features/manage-organization/settings/settings.service';
import { FormatMessagePipe } from './features/notifications/pipes/format-message.pipe';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule, FormatMessagePipe],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [
    { provide: NZ_I18N, useValue: en_US },
    BroadcastNotificationService,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'itb_frontend_angular';
  orgData: OrganizationDetailModel | null = null;
  reportLabels$: Observable<ReportLabel[]>;
  activeAlerts$: Observable<Broadcast[]>;
  private readonly USER_KEY = 'user_data';
  private readonly destroy$ = new Subject<void>();
  private readonly routeChanged$ = new Subject<string>();
  private lastSignalRInitTime = 0;
  private readonly minSignalRInitInterval = 10000;
  private initialConnectionMade = false;
  isHomePage = false;
  private isHandlingCrossTabLogout = false;
  private storageEventTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly i18n: NzI18nService,
    private readonly noteService: NoteService,
    private readonly authService: AuthService,
    private readonly reportLabelsService: ReportLabelsService,
    private readonly appService: AppService,
    public router: Router,
    private readonly broadcastService: BroadcastService,
    private readonly broadcastNotificationService: BroadcastNotificationService,
    private readonly commonService: CommonService,
    private readonly settingsService: SettingsService
  ) {
    this.i18n.setLocale(en_US);
    this.setupStorageListener();
    this.isHomePage = this.router.url === '/';
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.isHomePage = event.urlAfterRedirects === '/';
      });
    this.reportLabels$ = this.reportLabelsService.reportLabels$;
    this.activeAlerts$ = this.broadcastService.alerts$;

    this.authService.authState$
      .pipe(
        takeUntil(this.destroy$),
        filter(state => state.isAuthenticated),
        switchMap(state => {
          return timer(1000).pipe(map(() => state));
        })
      )
      .subscribe(() => {
        const isExcluded = this.broadcastNotificationService.isExcludedRoute(
          this.router.url
        );
        const user = localStorage.getItem(this.USER_KEY);
        if (user) {
          const userData = this.authService.decryptData(user);
          this.commonService.getOrgList(userData);
          this.loadOrganizationData(null);
        }
        if (!isExcluded && user) {
          this.safeInitializeSignalR();
        }
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
        debounceTime(1000)
      )
      .subscribe((event: NavigationEnd) => {
        const currentRoute = event.urlAfterRedirects;
        this.broadcastNotificationService.setCurrentRoute(currentRoute);
        this.routeChanged$.next(currentRoute);
      });

    this.setupSubscriptions();

    this.appService.networkStatus$
      .pipe(
        takeUntil(this.destroy$),
        filter(isOnline => isOnline),
        debounceTime(2000)
      )
      .subscribe(isOnline => {
        if (isOnline) {
          const isExcluded = this.broadcastNotificationService.isExcludedRoute(
            this.router.url
          );
          if (!isExcluded && this.shouldInitializeSignalR()) {
            this.safeInitializeSignalR();
          }
        }
      });

    this.appService.notificationReceived$
      .pipe(
        takeUntil(this.destroy$),
        filter(notification => !!notification && !!notification.body),
        debounceTime(1000),
        filter(() => {
          const isExcluded = this.broadcastNotificationService.isExcludedRoute(
            this.router.url
          );
          return !isExcluded;
        })
      )
      .subscribe(notification => {
        if (notification.body) {
          this.broadcastNotificationService.handleNotification(
            notification.body
          );
        }
      });
  }

  private safeInitializeSignalR(): void {
    const now = Date.now();
    if (!this.initialConnectionMade) {
      this.initialConnectionMade = true;
      this.lastSignalRInitTime = now;
      this.appService.initializeSignalR();
      return;
    }

    if (now - this.lastSignalRInitTime < this.minSignalRInitInterval) {
      return;
    }

    this.lastSignalRInitTime = now;
    this.appService.initializeSignalR();
  }

  private shouldInitializeSignalR(): boolean {
    const authState = this.authService.getCurrentAuthState();
    const user = localStorage.getItem(this.USER_KEY);
    return authState.isAuthenticated && !!user;
  }

  ngOnInit(): void {
    this.routeChanged$.next(this.router.url);
    this.appService.signalRConnected$
      .pipe(
        takeUntil(this.destroy$),
        filter(connected => connected)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.broadcastNotificationService.fetchBroadcastData(
            this.orgData?.organizationId || null
          );
        }, 1000);
      });
  }

  private setupStorageListener(): void {
    globalThis.addEventListener('storage', (event: StorageEvent) => {
      // Ignore null events or events without a key
      if (!event?.key) {
        return;
      }

      // Only handle user_data removal (logout)
      if (
        event.key === this.USER_KEY &&
        event.newValue === null &&
        event.oldValue !== null
      ) {
        // Debounce to prevent multiple rapid-fire events in Chrome
        if (this.storageEventTimeout) {
          clearTimeout(this.storageEventTimeout);
        }

        this.storageEventTimeout = setTimeout(() => {
          // Double-check that we're still logged in before handling logout
          const currentAuthState = this.authService.getCurrentAuthState();
          if (
            currentAuthState.isAuthenticated &&
            !this.isHandlingCrossTabLogout
          ) {
            this.handleCrossTabLogout();
          }
          this.storageEventTimeout = null;
        }, 100);
      }
    });
  }

  private handleCrossTabLogout(): void {
    // Prevent multiple simultaneous executions
    if (this.isHandlingCrossTabLogout) {
      return;
    }

    this.isHandlingCrossTabLogout = true;

    // Clear local state
    this.authService.clearLocalStateOnly();

    // Disconnect SignalR
    this.appService.disconnectSignalR().catch(() => {
      // Ignore errors during cleanup
    });

    // Navigate to login
    this.router.navigate(['/login'], { replaceUrl: true }).finally(() => {
      // Reset flag after a delay to allow navigation to complete
      setTimeout(() => {
        this.isHandlingCrossTabLogout = false;
      }, 1000);
    });
  }

  ngOnDestroy(): void {
    if (this.storageEventTimeout) {
      clearTimeout(this.storageEventTimeout);
      this.storageEventTimeout = null;
    }
    this.broadcastNotificationService.clearAllNotificationStates();
    this.destroy$.next();
    this.destroy$.complete();
    this.routeChanged$.complete();
    this.appService.disconnectSignalR();
  }

  private setupSubscriptions(): void {
    const validRoute$ = this.routeChanged$.pipe(
      distinctUntilChanged(),
      debounceTime(1000),
      map(url => ({
        url,
        isExcluded: this.broadcastNotificationService.isExcludedRoute(url),
      })),
      takeUntil(this.destroy$),
      share()
    );

    validRoute$.subscribe(({ isExcluded }) => {
      if (isExcluded) {
        this.appService.disconnectSignalR();
      } else if (this.shouldInitializeSignalR()) {
        this.safeInitializeSignalR();
        this.broadcastNotificationService.setupBroadcastNotifications();
      }
    });

    combineLatest([
      validRoute$,
      this.authService.orgData$.pipe(
        distinctUntilChanged(
          (prev, curr) => prev?.organizationId === curr?.organizationId
        )
      ),
    ])
      .pipe(
        debounceTime(1000),
        filter(([routeInfo]) => !routeInfo.isExcluded),
        map(([, orgData]) => orgData),
        takeUntil(this.destroy$)
      )
      .subscribe(organization => {
        const orgId = organization?.organizationId || null;
        this.orgData = organization;

        this.broadcastNotificationService.setOrganizationId(orgId);
        if (organization?.organizationId) {
          this.loadOrganizationData(organization);
        }
      });
  }

  private loadOrganizationData(
    organization: OrganizationDetailModel | null
  ): void {
    const orgId = organization?.organizationId || null;

    this.noteService.loadNotes({
      page: 1,
      pageSize: 5000,
      organizationId: orgId || 0,
    });

    this.reportLabelsService.loadReportLabels(orgId);
    this.settingsService.getOptions(orgId ?? 0).subscribe();
    this.broadcastNotificationService.fetchBroadcastData(orgId);
  }

  private loadGlobalData(): void {
    this.broadcastNotificationService.fetchBroadcastData(null);
  }
}
